import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { UserBadge } from '../components/auth/UserBadge'
import { StratIQXHeader } from '../components/ui/StratIQXHeader'
import { useAuthStore } from '../stores/authStore'
import { 
  Building2, 
  DollarSign, 
  Truck, 
  Target, 
  Database, 
  FileText,
  ArrowRight,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Shield,
  Lightbulb,
  TrendingUp
} from 'lucide-react'

export const OnboardingInfoPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  const handleStartOnboarding = () => {
    if (isAuthenticated) {
      navigate('/onboarding')
    } else {
      navigate('/auth')
    }
  }

  const handleBackToHome = () => {
    navigate('/')
  }

  const onboardingSteps = [
    {
      step: 1,
      icon: <Building2 className="h-6 w-6" />,
      title: 'Industry Selection',
      description: 'Choose your industry for a customized intelligence experience',
      details: 'Select from automotive, construction, heavy equipment, transportation, manufacturing, or professional services to get industry-specific insights.',
      timeEstimate: '2 minutes'
    },
    {
      step: 2,
      icon: <Users className="h-6 w-6" />,
      title: 'Business Profile',
      description: 'Industry-specific business information collection',
      details: 'Provide key business details including company size, market position, primary services, and operational focus areas.',
      timeEstimate: '5 minutes'
    },
    {
      step: 3,
      icon: <DollarSign className="h-6 w-6" />,
      title: 'Revenue Intelligence',
      description: 'Revenue stream analysis and monitoring setup',
      details: 'Configure tracking for your revenue sources, seasonal patterns, and key performance metrics to identify growth opportunities.',
      timeEstimate: '8 minutes'
    },
    {
      step: 4,
      icon: <Truck className="h-6 w-6" />,
      title: 'Supply Chain Intelligence',
      description: 'Supplier and cost tracking configuration',
      details: 'Set up monitoring for supplier relationships, cost fluctuations, and supply chain risks to optimize procurement strategies.',
      timeEstimate: '6 minutes'
    },
    {
      step: 5,
      icon: <Target className="h-6 w-6" />,
      title: 'Competitive Intelligence',
      description: 'Local market and competitor analysis setup',
      details: 'Define your competitive landscape, key rivals, and market positioning to stay ahead of industry changes.',
      timeEstimate: '7 minutes'
    },
    {
      step: 6,
      icon: <Database className="h-6 w-6" />,
      title: 'Data Integration',
      description: 'File uploads and data source connections',
      details: 'Upload historical business data, connect existing systems, and configure automated data feeds for comprehensive analysis.',
      timeEstimate: '10 minutes'
    },
    {
      step: 7,
      icon: <FileText className="h-6 w-6" />,
      title: 'Intelligence Delivery',
      description: 'Configure reporting preferences and delivery methods',
      details: 'Set up how you want to receive insights - weekly reports, real-time alerts, monthly summaries, or custom dashboards.',
      timeEstimate: '4 minutes'
    },
    {
      step: 8,
      icon: <BarChart3 className="h-6 w-6" />,
      title: 'Intelligence Summary',
      description: 'Review and finalize your intelligence profile',
      details: 'Review your complete intelligence setup, customize final preferences, and activate your strategic intelligence platform.',
      timeEstimate: '3 minutes'
    }
  ]

  const benefits = [
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-600" />,
      title: 'Market Change Predictions',
      description: 'Get early warnings about market shifts, regulatory changes, and industry trends that could impact your business.'
    },
    {
      icon: <DollarSign className="h-8 w-8 text-green-600" />,
      title: 'Cost Optimization',
      description: 'Identify opportunities to reduce costs through supplier intelligence, market timing, and operational efficiency insights.'
    },
    {
      icon: <Shield className="h-8 w-8 text-purple-600" />,
      title: 'Risk Mitigation',
      description: 'Proactively identify and prepare for potential supply chain disruptions, market downturns, and competitive threats.'
    },
    {
      icon: <Lightbulb className="h-8 w-8 text-orange-600" />,
      title: 'Growth Opportunities',
      description: 'Discover new market segments, partnership opportunities, and expansion possibilities based on data-driven insights.'
    }
  ]

  const totalTime = onboardingSteps.reduce((total, step) => {
    const minutes = parseInt(step.timeEstimate.split(' ')[0])
    return total + minutes
  }, 0)

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex justify-between items-center mb-4">
            <Button 
              variant="ghost" 
              onClick={handleBackToHome}
              className="text-blue-600 hover:text-blue-800"
            >
              ← Back to Home
            </Button>
            
            {/* Show user badge if authenticated */}
            {isAuthenticated && <UserBadge />}
          </div>
          
          <StratIQXHeader
            subtitle="Intelligence Setup"
            title="Configure Your Strategic Intelligence Platform"
            description="Comprehensive setup process for your customized StratIQX intelligence platform"
          />
          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>Total time: ~{totalTime} minutes</span>
            </div>
            <span>•</span>
            <span>8 personalized steps</span>
            <span>•</span>
            <span>Industry-specific configuration</span>
          </div>
        </div>

        {/* Benefits Overview */}
        <div className="mb-12">
          <Card className="shadow-lg border-0 bg-white">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                What You'll Get From StratIQX Intelligence
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="text-center">
                    <div className="flex justify-center mb-4">
                      {benefit.icon}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Onboarding Process Overview */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Your StratIQX Intelligence Setup Journey
          </h2>
          <div className="grid lg:grid-cols-2 gap-6">
            {onboardingSteps.map((step, index) => (
              <Card key={index} className="shadow-md border-0 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-600 rounded-lg p-3 flex-shrink-0">
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs font-semibold px-2 py-1 rounded">
                          Step {step.step}
                        </span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {step.timeEstimate}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 mb-3 text-sm">
                        {step.description}
                      </p>
                      <p className="text-gray-500 text-xs leading-relaxed">
                        {step.details}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Process Features */}
        <div className="mb-12">
          <Card className="shadow-lg border-0 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-center">
                StratIQX Intelligent Setup Features
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-90" />
                  <h3 className="font-semibold mb-2">Smart Progress Tracking</h3>
                  <p className="text-blue-100 text-sm">
                    Your progress is automatically saved at each step, so you can return anytime to complete the setup.
                  </p>
                </div>
                <div className="text-center">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-90" />
                  <h3 className="font-semibold mb-2">Industry Customization</h3>
                  <p className="text-blue-100 text-sm">
                    Each step adapts to your industry, showing relevant questions and intelligence opportunities.
                  </p>
                </div>
                <div className="text-center">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-90" />
                  <h3 className="font-semibold mb-2">Secure & Private</h3>
                  <p className="text-blue-100 text-sm">
                    All your business information is encrypted and securely stored with enterprise-grade security.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Card className="shadow-xl border-0 bg-white max-w-2xl mx-auto">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {isAuthenticated ? 'Continue Your StratIQX Setup' : 'Ready to Setup Your StratIQX Intelligence Platform?'}
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {isAuthenticated 
                  ? 'You\'re all set to continue with your personalized intelligence setup. Complete the remaining steps to activate your strategic intelligence platform.'
                  : `Start your personalized intelligence setup and get strategic insights tailored specifically 
                     for your industry and business needs. The entire process takes about ${totalTime} minutes and 
                     can be completed at your own pace.`
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleStartOnboarding}
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-3 flex items-center gap-2 shadow-lg"
                >
                  {isAuthenticated ? 'Continue Setup' : 'Start Intelligence Setup'}
                  <ArrowRight className="h-5 w-5" />
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleBackToHome}
                  size="lg"
                  className="px-8 py-3 border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  Learn More First
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                No commitment required • Cancel anytime • Industry expertise included
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
